import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeleteRegular, DismissCircleRegular, MoreVerticalFilled, SaveRegular } from '@fluentui/react-icons';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box, ButtonGroup, Grid2 as Grid, IconButton, MenuItem, Tooltip, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SIconButtonMenu from '@ui/components/common/SIconButtonMenu';
import { AssetForm } from '@ui/components/Forms';
import { PageHeader } from '@ui/layout/Layout/components';
import { assetApi } from '@application/api/endpoints/asset-api';
import { CustomDialog } from '@ui/components/Dialogs';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Incidents, ManualEntries, Systems } from './panels';
import useAssetsDetailStyles from './useAssetsDetailStyles';
import { useDispatch } from 'react-redux';
import { resetPagination as resetAssetSpecificPagination } from '@application/redux/asset-specific-incidents/asset-incidents-slice';
import { refreshProjects } from '@application/redux/Refresh/refreshSlice';
const initialDialogStates = {
    show: false,
    variant: 'delete',
    goBack: false
};
const AssetsDetail = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { asset } = useParams();
    const id = asset;
    let navigateBack = false;
    const { assetFormContainerStyles, saveButtonStyles, discardButtonStyles, iconButtonStyles, leftSectionGridStyles } = useAssetsDetailStyles();
    const [assetName, setAssetName] = useState('');
    const [tabValue, setTabValue] = useState('1');
    const [trigger, setTrigger] = useState(false);
    const [discardChanges, setDiscardChanges] = useState(false);
    const [hasDirtyValues, setHasDirtyValues] = useState(false);
    const [allowWindowNavigation, setAllowWindowNavigation] = useState(false);
    const [dialogOptions, setDialogOptions] = useState(initialDialogStates);
    const dispatch = useDispatch();
    const handleShowDialog = ({ show, variant, goBack }) => {
        setDialogOptions((prev) => ({
            ...prev,
            show,
            variant,
            goBack
        }));
    };
    const handleCloseDialog = () => {
        setDialogOptions(initialDialogStates);
        setDiscardChanges(false);
        setTrigger(false);
    };
    const handleConfirm = () => {
        setTrigger(true);
    };
    const handleDiscardChanges = () => {
        setDiscardChanges(true);
    };
    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
        dispatch(resetAssetSpecificPagination());
    };
    const handleAssetNameChange = (asset) => {
        setAssetName(asset);
    };
    const handleDelete = async () => {
        try {
            var response = await assetApi.deleteAssetDetailbyId(id);
            if (response.isSuccess) {
                toast.success(response.message);
                dispatch(refreshProjects());
                navigate(`/detail-asset`);
            }
        }
        catch (error) {
            toast.error(t('SomethingWentWrong'));
        }
        finally {
        }
    };
    useEffect(() => {
        const handleBackPress = (e) => {
            if (allowWindowNavigation) {
                const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
                if (confirmLeave) {
                    navigate(`/detail-asset`);
                    navigateBack = true;
                }
                else {
                    window.history.pushState(null, '', window.location.href);
                }
            }
        };
        if (allowWindowNavigation) {
            window.history.pushState(null, '', window.location.href);
        }
        else {
            window.history.replaceState(null, '', window.location.href);
        }
        window.addEventListener('popstate', handleBackPress);
        return () => {
            window.removeEventListener('popstate', handleBackPress);
        };
    }, [allowWindowNavigation, navigate]);
    useEffect(() => {
        window.addEventListener('popstate', function (event) {
            if (navigateBack) {
                navigate(`/detail-asset`);
            }
            else {
                if (!allowWindowNavigation) {
                    navigate(`/detail-asset`);
                }
                else {
                    navigate(`/detail-asset/${id}`);
                }
            }
        });
    }, [allowWindowNavigation, navigateBack]);
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (allowWindowNavigation) {
                const message = 'You have unsaved changes. Are you sure you want to leave?';
                event.returnValue = message; // Standard for most browsers
                return message;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [allowWindowNavigation]);
    const resetDialogOptions = () => setDialogOptions(initialDialogStates);
    return (_jsxs(React.Fragment, { children: [_jsx(PageHeader, { breadcrumbs: 'Assets', title: assetName, goBack: hasDirtyValues
                    ? () => handleShowDialog({
                        show: true,
                        variant: 'discard',
                        goBack: true
                    })
                    : true, icons: _jsxs(_Fragment, { children: [_jsxs(ButtonGroup, { variant: 'outlined', "aria-label": 'Basic button group', children: [_jsx(Tooltip, { title: 'Save Changes', arrow: true, children: _jsx(IconButton, { sx: {
                                            ...saveButtonStyles,
                                            cursor: !hasDirtyValues ? 'not-allowed' : undefined,
                                            opacity: !hasDirtyValues ? 0.5 : 1,
                                            pointerEvents: !hasDirtyValues ? 'auto' : undefined
                                        }, onClick: handleConfirm, disabled: !hasDirtyValues, children: _jsx(SaveRegular, { fontSize: 24, color: theme.palette.info.dark }) }) }), _jsx(Tooltip, { title: 'Discard Changes', arrow: true, children: _jsx(IconButton, { sx: {
                                            ...discardButtonStyles,
                                            cursor: !hasDirtyValues ? 'not-allowed' : undefined,
                                            opacity: !hasDirtyValues ? 0.5 : 1,
                                            pointerEvents: !hasDirtyValues ? 'auto' : undefined
                                        }, onClick: () => handleShowDialog({
                                            show: true,
                                            variant: 'discard',
                                            goBack: false
                                        }), disabled: !hasDirtyValues, children: _jsx(DismissCircleRegular, { fontSize: 24, color: theme.palette.error.dark }) }) })] }), _jsx(SIconButtonMenu, { sx: iconButtonStyles, menuItems: _jsx(_Fragment, { children: _jsxs(MenuItem, { onClick: handleDelete, sx: { color: (theme) => theme.palette.error.main }, children: [_jsx(DeleteRegular, { fontSize: 20 }), " ", t('DeleteAsset')] }) }), children: _jsx(MoreVerticalFilled, { fontSize: 24, color: theme.palette.action.active }) })] }) }), _jsx(Grid, { container: true, spacing: 3, className: 'main-grid', children: _jsxs(Grid, { className: 'left-section', size: { xs: 12, md: 8, xl: 9 }, rowSpacing: 3, sx: leftSectionGridStyles, height: 'calc(100dvh - 240px)', overflow: 'auto', children: [_jsx(Box, { className: 'asset-form-container', sx: assetFormContainerStyles, children: _jsx(AssetForm, { assetId: id, trigger: trigger, discardChanges: discardChanges, setDiscardChanges: setDiscardChanges, goBack: dialogOptions.goBack, setTrigger: setTrigger, onClose: handleCloseDialog, onAssetChange: handleAssetNameChange, setHasDirtyValues: setHasDirtyValues, resetDialogOptions: resetDialogOptions, setAllowWindowNavigation: setAllowWindowNavigation }) }), _jsx(Box, { sx: {
                                border: `1px solid ${theme.palette.action.active}`,
                                background: theme.palette.background.paper,
                                px: 2,
                                py: 3,
                                mt: 1,
                                borderRadius: 1.5
                            }, children: _jsxs(TabContext, { value: tabValue, children: [_jsx(Box, { sx: { mb: 2 }, children: _jsxs(TabList, { onChange: handleChangeTab, "aria-label": 'tabs-associated-with-asset', children: [_jsx(Tab, { label: t('Systems'), value: '1' }), _jsx(Tab, { label: t('Incidents'), value: '2' }), _jsx(Tab, { label: t('ManualEntries'), value: '3' })] }) }), _jsx(TabPanel, { value: '1', children: _jsx(Systems, {}) }), _jsx(TabPanel, { value: '2', children: _jsx(Incidents, {}) }), _jsx(TabPanel, { value: '3', children: _jsx(ManualEntries, {}) })] }) })] }) }), dialogOptions.show && (_jsx(CustomDialog, { isOpen: dialogOptions.show, onClose: handleCloseDialog, variant: dialogOptions.variant, actionText: 'Save or Discard Asset', screenName: assetName, closeBtnText: t('Cancel'), secondaryBtnText: t('DiscardChanges'), primaryBtnText: t('SaveChanges'), onSecondaryBtnClick: handleDiscardChanges, onPrimaryBtnClick: handleConfirm, loading: false, secondaryBtnLoading: false }))] }));
};
export default AssetsDetail;
